import type { CexFeeBreakdown, CexFeeCoinLeg } from "@/api/types";
import { getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS, CCC_FUSD_ADDRESS } from "@/config/env";

export function formatCexFeeCoinSymbol(coinSymbol: string): string {
  const u = (coinSymbol || "").toUpperCase();
  if (u === "USDT") return "USDT";
  if (u === "FUSD") return getTokenName(CCC_FUSD_ADDRESS) || "FUSD";
  if (u === "TSK" || u === "AIF") return getTokenName(CCC_AIF_ADDRESS) || "TSK";
  return coinSymbol;
}

export function formatCexTradingPair(symbol: string): string {
  if (!symbol) return "--";
  const u = symbol.toUpperCase();
  if (u.endsWith("USDT") && u.length > 4) {
    return `${u.slice(0, -4)}/USDT`;
  }
  return symbol;
}

export function formatFeeAmount(amount: number): string {
  if (!Number.isFinite(amount)) return "0";
  return amount % 1 === 0 ? amount.toFixed(1) : amount.toFixed(2);
}

export function formatFeeUsd(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return value.toFixed(1);
}

export function hasFeeBreakdown(b?: CexFeeBreakdown | null): boolean {
  if (!b) return false;
  if ((b.legs?.length ?? 0) > 0) return true;
  return (b.totalUsdt ?? 0) > 0;
}

export function isUsdtCoinSymbol(coinSymbol: string): boolean {
  return (coinSymbol || "").toUpperCase() === "USDT";
}

export type FeeLegDisplayLine = {
  key: string;
  text: string;
};

/** 将 breakdown 的 legs 格式化为展示行（带 +/- 前缀由调用方加） */
export function formatFeeBreakdownLegs(
  breakdown: CexFeeBreakdown | undefined,
  keyPrefix: string
): FeeLegDisplayLine[] {
  const legs = breakdown?.legs ?? [];
  if (legs.length > 0) {
    return legs.map((leg, i) => ({
      key: `${keyPrefix}-leg-${i}`,
      text: formatFeeLegText(leg),
    }));
  }
  const total = breakdown?.totalUsdt ?? 0;
  if (total > 0) {
    return [{ key: `${keyPrefix}-total`, text: `$${formatFeeUsd(total)}` }];
  }
  return [];
}

export function formatFeeLegText(leg: CexFeeCoinLeg): string {
  const label = formatCexFeeCoinSymbol(leg.coinSymbol);
  const amountStr = formatFeeAmount(leg.amount);
  if (isUsdtCoinSymbol(leg.coinSymbol)) {
    return `${amountStr} ${label}`;
  }
  const usd = leg.valueUsdt ?? 0;
  if (usd > 0) {
    return `${amountStr} ${label} (≈$${formatFeeUsd(usd)})`;
  }
  return `${amountStr} ${label}`;
}
